import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Video, X, Circle, Square, Loader2 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

// Keep frontend upload limit in sync with server MAX_VIDEO_SIZE_MB default (500MB)
const MAX_UPLOAD_MB = 500;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const extractVideoMetadata = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      const metadata = {
        durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
        width: video.videoWidth || null,
        height: video.videoHeight || null
      };
      URL.revokeObjectURL(url);
      resolve(metadata);
    };
    video.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
  });
};

// Helper function to detect mobile devices
const isMobileDevice = () => {
  // Check screen width (mobile-first approach)
  if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
    return true;
  }
  // Fallback: check user agent for mobile devices
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export default function UploadVideo({ file, onSelect, onClear }) {
  const { t, i18n } = useTranslation();
  const inputRef = useRef(null);
  const recordInputRef = useRef(null); // Input for native camera recording on mobile
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessingMetadata, setIsProcessingMetadata] = useState(false);
  const mountedRef = useRef(true);
  
  // Video recording state
  const [isRecording, setIsRecording] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const isCancellingRef = useRef(false); // Track if user cancelled vs stopped
  const recordingTimeRef = useRef(0); // Store recording time in ref for onstop handler

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const attachMetadataAndSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) {
      onSelect(null);
      setIsProcessingMetadata(false);
      return;
    }
    
    // Validate file size before processing (prevents unnecessary network transfer)
    const fileSizeBytes = selectedFile.size || 0;
    if (fileSizeBytes > MAX_UPLOAD_BYTES) {
      const sizeMB = Number((fileSizeBytes / 1024 / 1024).toFixed(2));
      const errorMessage = i18n.language === 'he'
        ? `הסרטון גדול מדי (${sizeMB}MB). אנא העלה סרטון עד ${MAX_UPLOAD_MB}MB. נסה לדחוס את הסרטון או להקליט קליפ קצר יותר.`
        : `Video is too large (${sizeMB}MB). Please keep uploads under ${MAX_UPLOAD_MB}MB. Try compressing the video or recording a shorter clip.`;
      alert(errorMessage);
      setIsProcessingMetadata(false);
      // Clear the input so user can try again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      if (recordInputRef.current) {
        recordInputRef.current.value = '';
      }
      return;
    }
    
    // Show loading state while processing metadata
    setIsProcessingMetadata(true);
    
    try {
      const metadata = await extractVideoMetadata(selectedFile);
      selectedFile.bodaiMeta = {
        durationSeconds: metadata.durationSeconds ? Math.round(metadata.durationSeconds) : null,
        width: metadata.width,
        height: metadata.height
      };
    } catch (err) {
      console.warn('Failed to read video metadata:', err);
      selectedFile.bodaiMeta = null;
    } finally {
      setIsProcessingMetadata(false);
    }
    
    if (!mountedRef.current) return;
    onSelect(selectedFile);
    setShowSuccess(true);
    setTimeout(() => {
      if (mountedRef.current) {
        setShowSuccess(false);
      }
    }, 3000);
  }, [onSelect, i18n.language]);

  const onPick = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    inputRef.current?.click();
  };
  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      attachMetadataAndSelect(f);
    } else {
      onSelect(null);
    }
    // Clear both inputs to allow selecting the same file again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (recordInputRef.current) {
      recordInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    if (!dt || !dt.files || dt.files.length === 0) return;
    const f = dt.files[0];
    if (f && f.type?.startsWith('video/')) {
      attachMetadataAndSelect(f);
    }
  }, [attachMetadataAndSelect]);

  // Hide success message when file is removed
  useEffect(() => {
    if (!file) {
      setShowSuccess(false);
    }
  }, [file]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }, 
        audio: true 
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9,opus'
      });

      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder.onstop fired', { 
          isCancelling: isCancellingRef.current,
          chunksLength: chunksRef.current.length,
          recordingTime: recordingTimeRef.current
        });
        
        // If user cancelled, don't process the recording
        if (isCancellingRef.current) {
          console.log('Recording cancelled, not processing video.');
          isCancellingRef.current = false; // Reset flag
          
          // Perform cleanup
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          setShowRecordModal(false);
          setIsRecording(false);
          setRecordingTime(0);
          recordingTimeRef.current = 0;
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          chunksRef.current = []; // Clear chunks
          return; // Exit early - don't create file or call onSelect
        }
        
        // Save recordingTime from ref (more reliable than state)
        const savedRecordingTime = recordingTimeRef.current;
        
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        
        // Validate blob has content
        if (!blob || blob.size === 0) {
          console.warn('Recording blob is empty, not processing');
          return;
        }
        
        // Convert blob to File
        const fileName = `recording-${Date.now()}.webm`;
        const videoFile = new File([blob], fileName, { type: 'video/webm' });
        
        // IMPORTANT: For recorded videos, we know the exact duration from our timer.
        // WebM blobs from MediaRecorder often have Infinity/NaN duration metadata,
        // so we use the recorded time directly instead of trying to extract it.
        const knownDurationSeconds = savedRecordingTime;
        
        // Validate duration
        if (!knownDurationSeconds || knownDurationSeconds <= 0) {
          console.warn('Recording duration is invalid:', knownDurationSeconds);
          // Don't proceed if duration is invalid
          return;
        }
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Clean up video element
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }

        // Close modal and reset state
        setShowRecordModal(false);
        setIsRecording(false);
        setRecordingTime(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        // For recorded videos, attach the known duration directly
        // (bypassing extractVideoMetadata which may fail for WebM blobs)
        videoFile.bodaiMeta = {
          durationSeconds: knownDurationSeconds,
          width: 1280, // We requested this resolution
          height: 720,
          isRecorded: true // Flag to indicate this was a recording
        };
        
        if (!mountedRef.current) return;
        onSelect(videoFile);
        setShowSuccess(true);
        setTimeout(() => {
          if (mountedRef.current) {
            setShowSuccess(false);
          }
        }, 3000);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimeRef.current = 0;
      timerRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime(recordingTimeRef.current);
      }, 1000);

    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check your permissions and try again.');
    }
  }, [attachMetadataAndSelect]);

  const stopRecording = useCallback(() => {
    console.log('stopRecording called', { 
      hasRecorder: !!mediaRecorderRef.current, 
      isRecording, 
      state: mediaRecorderRef.current?.state 
    });
    
    if (mediaRecorderRef.current) {
      // Check if recorder is actually recording
      if (mediaRecorderRef.current.state === 'recording') {
        // Mark that this is a proper stop (not cancel)
        isCancellingRef.current = false;
        try {
          mediaRecorderRef.current.stop();
          console.log('MediaRecorder.stop() called');
        } catch (err) {
          console.error('Error stopping media recorder:', err);
        }
      } else {
        console.warn('MediaRecorder is not in recording state:', mediaRecorderRef.current.state);
      }
    } else {
      console.warn('No mediaRecorderRef.current available');
    }
  }, [isRecording]);

  const cancelRecording = useCallback(() => {
    // If recording, ask user if they want to save or cancel
    if (mediaRecorderRef.current && isRecording && recordingTime > 0) {
      // User is recording - give them option to save or cancel
      const timeStr = formatTime(recordingTime);
      const message = i18n.language === 'he'
        ? (recordingTime >= 30 
            ? `הקלטת ${timeStr}. האם לשמור את ההקלטה?`
            : `הקלטת ${timeStr}. האם לשמור את ההקלטה? (מינימום 30 שניות לניתוח)`)
        : (recordingTime >= 30
            ? `Recorded ${timeStr}. Save the recording?`
            : `Recorded ${timeStr}. Save the recording? (Minimum 30 seconds for analysis)`);
      
      const shouldSave = window.confirm(message);
      
      if (shouldSave) {
        // User wants to save - stop recording normally
        console.log('User chose to save recording');
        isCancellingRef.current = false;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          try {
            mediaRecorderRef.current.stop();
            console.log('MediaRecorder.stop() called from cancelRecording (save)');
            // Don't close modal yet - onstop will handle it
            return;
          } catch (err) {
            console.error('Error stopping media recorder from cancelRecording:', err);
            // If stop fails, continue with cancel logic
          }
        } else {
          console.warn('Cannot save: MediaRecorder not available or not recording', {
            hasRecorder: !!mediaRecorderRef.current,
            state: mediaRecorderRef.current?.state
          });
          // If we can't stop, continue with cancel logic
        }
      }
      // User wants to cancel - continue with cancel logic below
    }
    
    // Mark that user is cancelling - don't process the recording
    isCancellingRef.current = true;
    
    if (mediaRecorderRef.current && isRecording) {
      // Stop the recorder first
      try {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.warn('Error stopping media recorder:', e);
      }
      // Clear chunks after a short delay to let onstop handler check the flag
      setTimeout(() => {
        chunksRef.current = [];
      }, 100);
    } else {
      // If not recording, just clear chunks immediately
      chunksRef.current = [];
    }
    
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clean up video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset state
    setShowRecordModal(false);
    setIsRecording(false);
    setRecordingTime(0);
  }, [isRecording, recordingTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRecordClick = (e) => {
    e.stopPropagation();
    
    // On mobile, use native camera (capture="user" for front camera)
    // On desktop, use our custom MediaRecorder modal
    if (isMobileDevice()) {
      recordInputRef.current?.click();
    } else {
      setShowRecordModal(true);
    }
  };

  return (
    <>
      {/* Success Toast Notification */}
      {showSuccess && file && (
        <div className="uploadSuccessToast">
          <div className="uploadSuccessToast__content">
            <CheckCircle2 className="uploadSuccessToast__icon" size={20} />
            <div className="uploadSuccessToast__text">
              <div className="uploadSuccessToast__title">{t('uploadVideo.successTitle')}</div>
              <div className="uploadSuccessToast__subtitle">{file.name}</div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`dropZone ${isDragging ? 'dropZone--active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onClick={onPick}
        aria-label={t('uploadVideo.ariaLabel')}
      >
        <div className="dropZone__icon">⭳</div>
        <div className="dropZone__title">{t('uploadVideo.dropTitle')}</div>
        <div className="dropZone__or">{t('uploadVideo.dropOr')}</div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button 
            type="button" 
            className="btn btn--primary" 
            onClick={(e) => {
              e.stopPropagation();
              onPick(e);
            }}
          >
            {t('uploadVideo.uploadButton')}
          </button>
          <button 
            type="button" 
            className="btn btn--secondary" 
            onClick={handleRecordClick}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Video size={18} />
            {t('uploadVideo.recordButton')}
          </button>
        </div>
        <div className="dropZone__meta">
          {t('uploadVideo.acceptedTypes', { size: MAX_UPLOAD_MB })}
        </div>
      </div>

      {/* Processing Metadata Loading Indicator */}
      {isProcessingMetadata && (
        <div className="uploadProcessingIndicator">
          <div className="uploadProcessingIndicator__content">
            <Loader2 className="uploadProcessingIndicator__spinner" size={20} />
            <div className="uploadProcessingIndicator__text">
              <div className="uploadProcessingIndicator__title">
                {t('uploadVideo.processingTitle', 'Processing video...')}
              </div>
              <div className="uploadProcessingIndicator__subtitle">
                {t('uploadVideo.processingSubtitle', 'Reading video details, this may take a few seconds')}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="uploadRow" style={{ marginBottom: 12 }}>
        {/* Regular file input for upload */}
        <input
          ref={inputRef}
          className="hiddenInput"
          type="file"
          accept="video/*"
          onChange={onChange}
          disabled={isProcessingMetadata}
        />
        {/* Native camera input for mobile recording (front camera) */}
        <input
          ref={recordInputRef}
          className="hiddenInput"
          type="file"
          accept="video/*"
          capture="user"
          onChange={onChange}
          disabled={isProcessingMetadata}
        />
        <div className="fileName" style={{ flex: 1 }}>
          {isProcessingMetadata ? (
            <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 
                size={16} 
                style={{ 
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block'
                }} 
              />
              {t('uploadVideo.processing', 'Processing...')}
            </span>
          ) : file ? (
            `${file.name} (${Math.round(file.size / 1024 / 1024 * 10) / 10} MB)`
          ) : (
            t('uploadVideo.noFile')
          )}
        </div>
        {file && !isProcessingMetadata && (
          <button
            className="pill-remove"
            onClick={onClear}
            aria-label={t('uploadVideo.removeFile')}
          >
            {t('uploadVideo.removeFile')}
          </button>
        )}
      </div>

      {/* Recording Modal */}
      {showRecordModal && (
        <div className="recordModal">
          <div className="recordModal__overlay" onClick={cancelRecording} />
          <div className="recordModal__content">
            <div className="recordModal__header">
              <h3>{t('uploadVideo.recordModalTitle')}</h3>
              <button 
                className="recordModal__close" 
                onClick={cancelRecording}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="recordModal__video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
              />
              {isRecording && (
                <div className="recordModal__timer">
                  <Circle size={12} fill="#ef4444" style={{ marginRight: '8px' }} />
                  {formatTime(recordingTime)}
                </div>
              )}
            </div>

            <div className="recordModal__actions">
              {!isRecording ? (
                <button 
                  className="btn btn--primary" 
                  onClick={startRecording}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Video size={18} />
                  {t('uploadVideo.startRecording')}
                </button>
              ) : (
                <button 
                  className="btn btn--danger" 
                  onClick={stopRecording}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Square size={18} />
                  {t('uploadVideo.stopRecording')}
                </button>
              )}
              <button 
                className="btn btn--ghost" 
                onClick={cancelRecording}
              >
                {t('uploadVideo.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

